import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Loader2 } from 'lucide-react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import EmailChangeDialog from './EmailChangeDialog';

// Edit a person's name and username. Email keeps its confirmation-link flow.
// Used by the Tenant Admin (Teams & Users) and the platform owner (Manage organisation, with tenantId).
const UserDetailsDialog = ({ open, onOpenChange, user, tenantId, onSaved }) => {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(user?.name || '');
    setUsername(user?.username || '');
    setBusy(false);
  }, [open, user]);

  const save = async (event) => {
    event.preventDefault();
    const payload = {};
    if (name.trim() && name.trim() !== (user?.name || '')) payload.name = name.trim();
    if (username.trim() && username.trim() !== (user?.username || '')) payload.username = username.trim();
    if (!Object.keys(payload).length) { onOpenChange(false); return; }
    if (tenantId) payload.tenant_id = tenantId;
    setBusy(true);
    try {
      const { data } = await axiosInstance.patch(`/users/${encodeURIComponent(user.id)}/details`, payload);
      toast.success('Details saved');
      onSaved?.(data);
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? 'Usernames use letters, numbers, dots, dashes or underscores (3–50 characters).' : detail || 'Could not save these details');
    } finally { setBusy(false); }
  };

  return <>
    <Dialog open={open && !emailOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="user-details-dialog">
        <DialogHeader>
          <DialogTitle>Edit details</DialogTitle>
          <DialogDescription>Changes are saved to the audit log. Past records keep the name used at the time.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={save}>
          <label className="block text-sm font-medium">Full name
            <Input className="mt-1" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} required autoFocus />
          </label>
          <label className="block text-sm font-medium">Username
            <Input className="mt-1" value={username} onChange={(event) => setUsername(event.target.value)} maxLength={50} placeholder="Not set" />
            <span className="mt-1 block text-xs font-normal text-gray-500">Used to sign in. Must be unique in this organisation.</span>
          </label>
          <div className="rounded-lg border border-gray-200 p-3 text-sm">
            <p className="text-xs text-gray-500">Sign-in email</p>
            <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
              <span className="truncate font-medium text-gray-900">{user?.email}</span>
              <Button type="button" size="sm" variant="outline" onClick={() => setEmailOpen(true)}><KeyRound size={14} className="mr-1.5" />Change email</Button>
            </div>
            <p className="mt-1 text-xs text-gray-500">The new address gets a confirmation link. Nothing changes until it's confirmed.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="qflow-accent-bg hover:opacity-90">{busy ? <Loader2 size={16} className="animate-spin" /> : 'Save'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    <EmailChangeDialog open={emailOpen} onOpenChange={(value) => { setEmailOpen(value); }} user={user} tenantId={tenantId} />
  </>;
};

export default UserDetailsDialog;
